import React, { MouseEvent } from 'react'
import { useNode, useEditor, UserComponent, Element } from '@craftjs/core'
import classnames from 'classnames'
import {createUseStyles} from 'react-jss'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import Cell from './Cell'

import {
  ILayout_Row,
  ILayout_Row_Config,
  ILayout_Options,
} from '@kaidam/fc-shared/src/types/layout'

import useEditorActions from './useEditorActions'

import {
  spacing,
  useRowStyles,
  isNodeRow,
} from './utils'

export const useStyles = createUseStyles({
  drag: {
    paddingRight: spacing(1),
    width: spacing(4),
    minWidth: spacing(4),
    flexGrow: 0,
    cursor: 'move',
  },
  dragInner: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  dragInnerButton: {
    flexGrow: 0,
  },
  dragInnerSpacer: {
    flexGrow: 1,
  },
  arrowButton: {
    padding: '0px'
  },
})

interface RowProps {
  row: ILayout_Row,
  config?: ILayout_Row_Config,
  options: ILayout_Options,
  className?: string,
}

const Row: UserComponent<RowProps> = ({
  row,
  options,
  className,
}) => {

  const { query } = useEditor()
  const {
    id,
    config,
    connectors: {connect, drag},
    topLevel,
  } = useNode(node => {
    const config: ILayout_Row_Config = node.data.props.config || {}
    return {
      id: node.id,
      topLevel: query.node(node.id).ancestors().length == 1,
      config,
    }    
  })

  const {
    moveRow,
  } = useEditorActions()

  const classes = useStyles()
  const rowClasses = useRowStyles({
    config: config || {},
    editorMode: true,
    topLevel,
  })

  const RowAny = Row as any
  const CellAny = Cell as any

  return (
    <div
      className={ classnames('Layout-Editor-Node', 'Layout-Editor-Row', className, rowClasses.row) }
      ref={ref => connect(drag(ref))}
    >
      <div
        className={ classnames('Layout-Editor-Row-Drag', classes.drag) }
      >
        <div
          className={ classnames('Layout-Editor-Row-Drag-Inner', classes.dragInner) }
        >
          {
            topLevel && !options.disableMove && (
              <>
                <div className={ classes.dragInnerButton }>
                  <Tooltip title="Move Up" arrow>
                    <IconButton
                      className={ classes.arrowButton }
                      size="small"
                      onClick={ (e: MouseEvent) => {
                        moveRow(id, -1)
                        return false
                      }}
                    >
                      <ArrowDropUpIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
                <div className={ classes.dragInnerSpacer }>

                </div>
                <div className={ classes.dragInnerButton }>
                  <Tooltip title="Move Down" arrow>
                    <IconButton
                      className={ classes.arrowButton }
                      size="small"
                      onClick={ (e: MouseEvent) => {
                        moveRow(id, 1)
                        return false
                      }}
                    >
                      <ArrowDropDownIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
              </>
            )
          }
        </div>
      </div>
      <div className={ rowClasses.cellContainer }>
        <div className={ rowClasses.margin }></div>
        <Element
          canvas
          id="rowinner"
          is="div"
          custom={{
            type: 'rowinner',
          }}
          className={ classnames('Row-Inner', rowClasses.cells) }
        >
          {
            row.cells.map((node, i) => {
              if(isNodeRow(node)) {
                return (
                  <RowAny
                    key={ i }
                    row={ node }
                    config={ node.config }
                    options={ options }
                  />
                )
              }
              else {
                return (
                  <CellAny
                    key={ i }
                    type={ node.type }
                    isNew={ node.isNew }
                    data={ node.data }
                    config={ node.config }
                    options={ options }
                  />
                )
              }
              
            })
          }
        </Element>
        <div className={ rowClasses.margin }></div>
      </div>
    </div>
  )
}

Row.craft = {
  displayName: 'row',
  defaultProps: {
    
  },
  rules: {
    canDrag: (node) => {
      if(node.data.props.options.disableMove) return false
      return true
    },
    canDrop: (target) => {
      return target.data.displayName == 'layout'
    }
  },
}

export default Row